
import { useRecoilValue } from "recoil";
import { CustomerListType, PermissionsType, SecretaryRequestType, SecretaryType, UserType } from "../../../types/types";
import { AbcOutlined, BadgeOutlined, CheckCircleRounded, CloseOutlined, DeleteForeverOutlined, DoNotDisturbOnRounded, DriveFileRenameOutlineOutlined, EmailOutlined, HelpOutlineOutlined, HideImageOutlined, InfoOutlined, KeyOutlined, LocalAirportOutlined, LocationCityOutlined, ManOutlined, Person, PersonOutlineOutlined, PhoneOutlined, PinOutlined, PlaceOutlined, ReduceCapacityOutlined, RemoveCircleOutlineOutlined, SaveOutlined, Tune, UndoOutlined } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Autocomplete, Box, Button, Chip, FormControl, Grid, IconButton, InputLabel, Paper, Skeleton, Step, StepContent, StepLabel, Stepper, TextField, Tooltip, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { ChangeEvent, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ContentProps, Toast } from "primereact/toast";
import { useCookies } from "react-cookie";
import { useLocation, useNavigate } from "react-router-dom";
import GridTemplate from "../../../components/GridTemplate";
import Rmodal from "../../../components/Rmodal";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";

import { CNPJFormatter, formatterZipCode, getFilteredList, phoneNumberFormatter, setToastRefToOpen } from "../../../functions/functions";
import secretaryUpdateStatusById from "../../../resources/Api/secretary/secretaryUpdateStatusById";
import secretaryOnUpdateData from "../../../resources/Api/secretary/secretaryOnUpdateData";
import secretaryOnSaveData from "../../../resources/Api/secretary/secretaryOnSaveData";
import secretaryFindAllCrudResponse from "../../../resources/Api/secretary/secretaryFindAllCrudResponse";
import { userAtom } from "../../../atoms/atoms";
import secretariesFindAllExportResponse from "../../../resources/Api/secretary/secretariesFindAllExportResponse";
import { customerDefault, MENUS_ITENS } from "../../../consts/consts";
import customerFindAllListResponse from "../../../resources/Api/customer/customerFindAllListResponse";
import useCepAutofill from "../../../hooks/useCepAutofill";

const columns: GridColDef[] = [
    {
        field: "activated",
        type: "boolean",
        width: 150,
        editable: false,
        sortable: false,
        renderHeader: () => (
            <Chip
                sx={{
                    color: "#fff",
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={"Ativo"}
                icon={
                    <KeyOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />
                }
            />
        ),
        renderCell: (item) => (
            <Chip
                sx={{ background: "transparent" }}
                icon={
                    item.row.activated ? (
                        <CheckCircleRounded
                            className="activated"
                            style={{ height: 28, width: 28 }}
                        />
                    ) : (
                        <DoNotDisturbOnRounded
                            className="deactivated"
                            style={{ height: 28, width: 28 }}
                        />
                    )
                }
            />
        ),
    },
    {
        field: "reducedName",
        align: "left",
        width: 250,
        editable: false,
        sortable: true,
        renderHeader: () => (
            <Chip
                sx={{
                    color: "#fff",
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={"Nome fantasia"}
                icon={<AbcOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />}
            />
        ),
        renderCell: (item) => (
            <Chip
                sx={{
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={item.row.reducedName}
            />
        ),
    },
    {
        field: "cnpj",
        width: 200,
        align: "left",
        editable: false,
        sortable: true,
        renderHeader: () => (
            <Chip
                sx={{
                    color: "#fff",
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={"CNPJ"}
                icon={
                    <BadgeOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />
                }
            />
        ),
        renderCell: (item) => (
            <Chip
                sx={{
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={CNPJFormatter(item.row.cnpj)}
            />
        ),
    },
    {
        field: "email",
        align: "left",
        width: 250,
        headerClassName: "last-column",
        editable: false,
        sortable: true,
        renderHeader: () => (
            <Chip
                sx={{
                    color: "#fff",
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={"E-mail"}
                icon={
                    <EmailOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />
                }
            />
        ),
        renderCell: (item) => (
            <Chip
                sx={{
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={item.row.email}
            />
        ),
    },
    {
        field: "accessIcon",
        sortable: false,
        headerName: "",
        width: 320,
        renderCell: (item) => {
            const getItemPermission = (id: number) => {
                return item.row.permissions.find((permission: PermissionsType) => {
                    if (permission.optionItem.id === id) {
                        const { optionItem, ...rest } = permission;
                        return rest;
                    };
                });
            };
            // const menuItemId = 8;
            const permissions = getItemPermission(Number(MENUS_ITENS.DEPARTMENTS));
            return (
                <>{permissions.canView && item.row.canshowOption && <Chip
                    className="chip-access"
                    sx={{
                        marginRight: 2,
                        fontFamily: "Prometo",
                        background: "#00B700",
                        color: "#ffff",
                        padding: 1,
                    }}
                    onClick={() => {
                        item.row.navigate?.("/customers/secretaries/departments", item.id, permissions);
                    }}
                    label="Departamentos"
                    icon={
                        <ReduceCapacityOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />
                    }
                />}</>
            )
        },
    },
];

const rowsOptions = (
    list: Array<SecretaryType>,
    permissions: Array<PermissionsType>,
    navigate: (...args: any) => void,
    canshowOption: boolean = true,
): Array<SecretaryType> => {
    const result = [...list];
    return result.map((item) => ({ ...item, navigate, permissions, canshowOption }));
};

/**
 * SecretaryPage - Component that renders a page to list, create and manage secretaries
 * @component
 */
const SecretaryPage = ({ isHiddenMenu }: { isHiddenMenu: boolean }) => {
    //atoms
    const getCurrentUser = useRecoilValue<UserType>(userAtom);

    // functionalities states
    const [isOnline, setIsOnline] = useState(true);
    const [loading, setLoading] = useState(false);
    const [validationText, setTextValidation] = useState<string>("");
    const [isFieldsValid, setFieldsIsValid] = useState<boolean>(true);
    const [statusMessage, setStatusMessage] = useState<"warn" | "success" | "info" | "error" | undefined>("warn");
    const [isToastVisible, setIsToastVisible] = useState(false);
    const [inEdit, setInEdit] = useState<boolean>(false);
    const [inInsert, setInInsert] = useState<boolean>(false);
    const [isOpenDetails, setIsOpenDetails] = useState<boolean>(false);
    const [query, setQuery] = useState<string>("");
    const [filteredList, setFilteredList] = useState<Array<SecretaryType>>([]);

    const [isLastPage, setIsLastPage] = useState<boolean>(true);
    const [numberOfElementsPerPage, setNumberOfElementsPerPage] = useState<number>(0);
    const [numberOfElements, setNumberOfElements] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(0);

    const [infoExpanded, setInfoExpanded] = useState<boolean>(true);
    const [info2Expanded, setInfo2Expanded] = useState<boolean>(false);

    // states
    const [secretariesList, setSecretariesList] = useState<Array<SecretaryType>>([]);
    const [selectedSecretary, setSelectedSecretary] = useState<SecretaryType>({
        id: -1,
    } as any);

    const [secretaryName, setSecretaryName] = useState<string>("");

    const [secretaryReducedName, setSecretaryReducedName] = useState<string | null>("");
    const [secretaryCnpj, setSecretaryCnpj] = useState<string | null>("");

    const [cep, setCep] = useState<string>("");
    const [secretaryAddress, setSecretaryAddress] = useState<string>("");
    const [secretaryPhone, setSecretaryPhone] = useState<string>("");
    const [secretaryPhone2, setSecretaryPhone2] = useState<string>("");
    const [secretaryEmail, setSecretaryEmail] = useState<string>("");

    const [customersList, setCustomersList] = useState<Array<CustomerListType>>([]);
    const [customer, setCustomer] = useState<CustomerListType>(customerDefault);

    const [report, setReport] = useState<ArrayBuffer>(new ArrayBuffer(0));
    const [exportType, setExportType] = useState<string>("");

    // refs
    // const fileInputRef = useRef<HTMLInputElement>(null);
    const toastModalRef = useRef<Toast>(null);
    const toastRef = useRef<Toast>(null);

    // cookies
    const [cookies, _] = useCookies(["selectedParameters", "token"]);

    // hooks e const
    const location = useLocation();
    const navigate = useNavigate();

    const handleClose = useCallback(() => {
        setLoading(false);
        setStatusMessage("warn");
        setIsToastVisible(false);
        setTextValidation("");
        setIsOpenDetails(false);
        setInEdit(false);
        setInInsert(false);
        setLoading(false);
        setStatusMessage("warn");
        setIsToastVisible(false);
        setTextValidation("");
        setIsOpenDetails(false);
        setSelectedSecretary({} as any);
        setInEdit(false);
        setInInsert(false);
        setSecretaryName("");
        setSecretaryReducedName("");
        setSecretaryCnpj("");
        setSecretaryAddress("");
        setCep("");
        setSecretaryEmail("");
        setSecretaryPhone("");
        setSecretaryPhone2("");
        setInfo2Expanded(false);
        setInfoExpanded(true);
    }, []);

    useEffect(() => {
        const ref = isOpenDetails ? toastModalRef : toastRef;
        if (validationText && !isFieldsValid && !isToastVisible) {
            setToastRefToOpen(
                ref,
                validationText,
                "Atenção!",
                statusMessage,
                undefined,
                3000,
                false
            );
        }
    }, [
        isOpenDetails,
        validationText,
        isFieldsValid,
        statusMessage,
        isToastVisible,
    ]);

    const goToRoute = useCallback(
        async (path: string, state?: any) => {
            const hasConection: boolean = window.navigator.onLine;
            if (hasConection) {
                navigate(path, { state: state });
            } else {
                setIsOnline(false);
            }
        },
        [navigate]
    );

    const getAllSecretaries = useCallback(
        async (status: string = "activated", page: number = 0, size: number = 100) => {
            const token: string = cookies.token || sessionStorage.getItem("token");
            const customerId = location?.state?.customerId || customer.id;

            setLoading(true);
            try {
                const { data } = await secretaryFindAllCrudResponse(token, customerId, status, page, size);
                setSecretariesList(data.content);
                setIsLastPage(data.last);
                setNumberOfElementsPerPage(data.numberOfElements);
                setPageNumber(data.pageable.pageNumber);

                if (data.first) {
                    setNumberOfElements(data.totalElements);
                }
            } catch (error: any) {
                console.log(error);
                setSecretariesList([]);
            } finally {
                setTimeout(() => {
                    setLoading(false);
                }, 2000);
            }
        },
        [cookies.token, location?.state?.customerId, location?.state, customer.id]
    );

    useEffect(() => {
        getAllSecretaries();
    }, [getAllSecretaries]);

    /** Endereco pelo CEP (ViaCEP, gratuito). A secretaria nao tem pais/estado/cidade, so o endereco. */
    const cepAutofill = useCepAutofill({
        cep,
        enabled: inInsert || inEdit,
        savedCep: selectedSecretary?.postalCode,
        inEdit,
        onAddress: (address) => {
            const text = [address.street, address.neighborhood, `${address.city}/${address.state}`]
                .filter(Boolean)
                .join(" - ");
            setSecretaryAddress(text.slice(0, 150));
        },
    });

    const onSave = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        const customerId = location?.state?.customerId || customer.id;
        setLoading(true);

        const data: SecretaryRequestType = {
            id: -1,
            name: secretaryName,
            reducedName: secretaryReducedName,
            phone1: secretaryPhone,
            phone2: secretaryPhone2,
            email: secretaryEmail,
            cnpj: secretaryCnpj,
            postalCode: cep,
            customerId: customerId,
            address: secretaryAddress,
        };

        if (
            !data.name ||
            !data.postalCode ||
            !data.address ||
            !data.phone1 ||
            !data.email
        ) {
            setTextValidation(
                "Todos os campos marcados com * são obrigatórios, preencha e tente novamente."
            );
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setLoading(false);
            return;
        }

        try {
            await secretaryOnSaveData(token, data);
            setTextValidation("Registro salvo com sucesso.");
            setStatusMessage("success");
            setFieldsIsValid(false);

            setTimeout(() => {
                handleClose();
                getAllSecretaries();
            }, 3000);
        } catch (error: any) {
            setLoading(false);
            const { data } = error.response;

            const errors = data.reason.split(".");
            errors.forEach((message: any, index: number) => {
                if (index === 0) {
                    setFieldsIsValid(false);
                    setStatusMessage("warn");
                    setTextValidation(message);
                }
            });
        }
    }, [
        cookies.token,
        secretaryName,
        secretaryReducedName,
        location?.state?.customerId,
        secretaryCnpj,
        cep,
        secretaryAddress,
        secretaryPhone,
        secretaryPhone2,
        secretaryEmail,
        handleClose,
        getAllSecretaries,
        customer.id
    ]);

    const onUpdate = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        const customerId = location?.state?.customerId || customer.id;

        setLoading(true);

        const data: SecretaryRequestType = {
            id: selectedSecretary.id,
            name: secretaryName,
            reducedName: secretaryReducedName,
            phone1: secretaryPhone,
            phone2: secretaryPhone2,
            email: secretaryEmail,
            cnpj: secretaryCnpj,
            postalCode: cep,
            customerId: customerId,
            address: secretaryAddress
        };

        if (
            !data.name ||
            !data.postalCode ||
            !data.address ||
            !data.phone1 ||
            !data.email
        ) {
            setTextValidation(
                "Todos os campos são obrigatórios, preencha e tente novamente."
            );
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setLoading(false);
            return;
        }

        if (
            data.name === selectedSecretary.name &&
            ((data.cnpj && data.reducedName === selectedSecretary.reducedName)?.valueOf() ?? true) &&
            data.cnpj === selectedSecretary?.cnpj &&
            data.postalCode === selectedSecretary.postalCode &&
            data.address === selectedSecretary.address &&
            data.phone1 === selectedSecretary.phone1 &&
            data.phone2 === selectedSecretary.phone2 &&
            data.email === selectedSecretary.email
        ) {
            setTextValidation("Nada foi alterado.");
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setLoading(false);
            return;
        }

        try {
            await secretaryOnUpdateData(token, data, data.id);
            setTextValidation("Registro atualizado com sucesso.");
            setStatusMessage("success");
            setFieldsIsValid(false);

            setTimeout(() => {
                handleClose();
                getAllSecretaries();
            }, 3000);
        } catch (error: any) {
            setLoading(false);
            const { data } = error.response;

            const errors = data.reason.split(".");
            errors.forEach((message: any, index: number) => {
                if (index === 0) {
                    setFieldsIsValid(false);
                    setStatusMessage("warn");
                    setTextValidation(message);
                }
            });
        }
    }, [
        cookies.token,
        secretaryName,
        secretaryReducedName,
        location?.state?.customerId,
        secretaryCnpj,
        cep,
        secretaryAddress,
        secretaryPhone,
        secretaryPhone2,
        secretaryEmail,
        selectedSecretary,
        handleClose,
        getAllSecretaries,
        customer.id
    ]);

    const onUpdateStatus = useCallback(
        async (id: number, status: string) => {
            const token: string = cookies.token || sessionStorage.getItem("token");
            setLoading(true);
            try {
                await secretaryUpdateStatusById(
                    token,
                    id,
                    status
                );
                setTextValidation("Registro atualizado com sucesso.");
                setStatusMessage("success");
                setFieldsIsValid(false);
            } catch (error: any) {
                const { data } = error.response

                setLoading(false);
                setFieldsIsValid(false);
                setStatusMessage("warn");
                setTextValidation(data.reason);
            } finally {
                setTimeout(() => {
                    handleClose();
                    getAllSecretaries();
                }, 8000);
            }
        },
        [cookies.token, getAllSecretaries, handleClose]
    );

    const getReport = useCallback(async (type: string) => {
        setLoading(true);
        const customerId = location?.state?.customerId || customer.id;
        let dataBuffer = new ArrayBuffer(0);

        let reportType = type;
        let isSimpleType = false;
        if (type.includes("simple")) {
            reportType = reportType.split(" ")[0];
            isSimpleType = true;
        };
        // simpleXlsxExport = false

        try {
            const { data } = await secretariesFindAllExportResponse(cookies.token, customerId,
                (secretariesList.length > 0 && secretariesList[0].activated)
                    ? "activated"
                    : "deactivated", reportType, isSimpleType);

            if ((data as ArrayBuffer).byteLength === 0) {
                setTextValidation("Nenhum dado para mostrar no relatório.");
                setStatusMessage("warn");
                setFieldsIsValid(false);
            } else {
                setTextValidation("Relatório gerado com sucesso.");
                setStatusMessage("success");
                setFieldsIsValid(false);
                dataBuffer = data;
            }
        } catch (error: any) {
            const { data } = error.response
            setLoading(false);
            setFieldsIsValid(false);
            setStatusMessage("warn");
            setTextValidation(data.reason);
        } finally {
            setTimeout(() => {
                handleClose();
                setReport(dataBuffer);
                setExportType(type);
            }, 3000);
        }
    }, [cookies.token, secretariesList, location?.state?.customerId, customer.id, handleClose]);

    useEffect(() => {
        if (report.byteLength > 1) {
            const typeExport = (exportType === "pdf" ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
            const file = new Blob([report], { type: typeExport });
            const fileURL = URL.createObjectURL(file);
            window.open(fileURL);
            setReport(new ArrayBuffer(0));
        }
    }, [report, exportType]);

    useEffect(() => {
        setFilteredList(
            getFilteredList(["reducedName", "cnpj", "email"], secretariesList, query)
        );
    }, [secretariesList, query]);

    const verifyBeforeCancel = useCallback(
        (selected: SecretaryType) => {
            const ref = toastModalRef;
            let canCancel = true;

            if (inEdit && !inInsert) {
                if (
                    secretaryName !== selected.name ||
                    (secretaryCnpj && secretaryReducedName !== selected.reducedName) ||
                    secretaryCnpj !== selected?.cnpj ||
                    secretaryAddress !== selected.address ||
                    secretaryPhone !== selected.phone1 ||
                    secretaryPhone2 !== selected.phone2 ||
                    secretaryEmail !== selected.email
                ) {
                    canCancel = false;
                }
            } else if (
                !inEdit &&
                inInsert &&
                (secretaryName ||
                    secretaryReducedName ||
                    secretaryCnpj ||
                    cep ||
                    secretaryAddress ||
                    secretaryPhone ||
                    secretaryPhone2 ||
                    secretaryEmail
                )
            ) {
                canCancel = false;
            }

            if (!canCancel) {
                if (!isToastVisible) {
                    setToastRefToOpen(
                        ref,
                        "Existem dados não salvos, deseja realmente cancelar?",
                        "Atenção!",
                        statusMessage,
                        {
                            content: (props: ContentProps) => (
                                <Box
                                    className="flex flex-column align-items-left"
                                    style={{ flex: "1" }}
                                >
                                    <Typography>{props?.message.summary}</Typography>
                                    <Box className="font-medium text-lg my-3 text-900">
                                        {props?.message.detail}
                                    </Box>
                                    <Button
                                        className="p-button-sm flex"
                                        style={{ cursor: "pointer", color: "inherit" }}
                                        onClick={() => {
                                            ref.current?.clear();
                                            handleClose();
                                        }}
                                    >
                                        Sim
                                    </Button>
                                    <Button
                                        className="p-button-sm flex"
                                        style={{ cursor: "pointer", color: "inherit" }}
                                        onClick={() => {
                                            ref.current?.clear();
                                            setIsToastVisible(false);
                                        }}
                                    >
                                        Não
                                    </Button>
                                </Box>
                            ),
                        },
                        undefined,
                        true
                    );
                    setIsToastVisible(true);
                }
            } else {
                handleClose();
            }
        },
        [
            inEdit,
            inInsert,
            handleClose,
            statusMessage,
            isToastVisible,
            cep,
            secretaryAddress,
            secretaryCnpj,
            secretaryEmail,
            secretaryName,
            secretaryReducedName,
            secretaryPhone,
            secretaryPhone2
        ]
    );

    const verifyBeforeUpdateStatus = useCallback(
        (id: number, status: string) => {
            const ref = toastModalRef;
            if (!isToastVisible) {
                setToastRefToOpen(
                    ref,
                    "Deseja realmente atualizar o status desse registro?",
                    "Atenção!",
                    statusMessage,
                    {
                        content: (props: ContentProps) => (
                            <Box
                                className="flex flex-column align-items-left"
                                style={{ flex: "1" }}
                            >
                                <Typography>{props?.message.summary}</Typography>
                                <Box className="font-medium text-lg my-3 text-900">
                                    {props?.message.detail}
                                </Box>
                                <Button
                                    className="p-button-sm flex"
                                    style={{ cursor: "pointer", color: "inherit" }}
                                    onClick={() => {
                                        ref.current?.clear();
                                        setIsToastVisible(false);
                                        onUpdateStatus(id, status);
                                    }}
                                >
                                    Sim
                                </Button>
                                <Button
                                    className="p-button-sm flex"
                                    style={{ cursor: "pointer", color: "inherit" }}
                                    onClick={() => {
                                        ref.current?.clear();
                                        setIsToastVisible(false);
                                    }}
                                >
                                    Não
                                </Button>
                            </Box>
                        ),
                    },
                    undefined,
                    true
                );
                setIsToastVisible(true);
            }
        },
        [onUpdateStatus, isToastVisible, statusMessage]
    );

    const handleOnEditValues = useCallback(async (selectedSecretary: SecretaryType) => {
        if (selectedSecretary?.name && !inInsert) {
            setSecretaryName(selectedSecretary.name);
            setSecretaryReducedName(selectedSecretary?.cnpj ? selectedSecretary?.reducedName : "");
            setSecretaryCnpj(selectedSecretary?.cnpj);
            setSecretaryAddress(selectedSecretary.address);
            setCep(selectedSecretary.postalCode);
            setSecretaryPhone(selectedSecretary.phone1);
            setSecretaryPhone2(selectedSecretary.phone2 ?? "");
            setSecretaryEmail(selectedSecretary.email);
        }
    }, [
        inInsert
    ]);

    const loadingEsqueleton = useMemo(() => {
        return (
            <Grid
                className="container "
                sx={{
                    height: 650,
                    width: 950,
                }}
            >
                {Array.from(new Array(1)).map((_, i) => (
                    <Box key={i} sx={{ width: "100%", height: 650, padding: 5 }}>
                        <Skeleton height={70} />
                        <Skeleton height={70} />
                        <Skeleton height={70} />
                        <Skeleton height={70} />
                        <Skeleton height={70} />
                        <Skeleton height={70} />
                    </Box>
                ))}
            </Grid>
        );
    }, []);

    const DetailsModal = useMemo(() => {
        return (
            <Rmodal
                open={isOpenDetails}
                hiddenBackdrop={false}
                toast={
                    <Toast
                        ref={toastModalRef}
                        position="center"
                        onShow={() => {
                            setIsToastVisible(true);

                            if (validationText) {
                                setTimeout(() => {
                                    setFieldsIsValid(true);
                                    setIsToastVisible(false);
                                }, 3000);
                            }
                        }}
                    />
                }
            >
                <Paper
                    className="tw-flex tw-flex-col tw-overflow-hidden tw-rounded-2xl tw-bg-white tw-shadow-2xl tw-transition-all tw-duration-300 tw-ease-gtr-smooth"
                    sx={style}
                >
                    <Box
                        className="row tw-m-0 tw-flex-1 tw-overflow-y-auto tw-bg-white tw-p-4 sm:tw-p-6"
                        sx={{
                            margin: 0,
                            width: "100%",
                            flex: 1,
                            overflowY: "auto",
                            backgroundColor: "#ffff",
                            boxShadow: "none",
                            padding: { xs: 2, sm: 3 },
                            scrollbarWidth: "thin",
                            scrollbarColor: "#006C77 transparent",
                            "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#ffff", transition: "box-shadow 200ms ease, background-color 200ms ease" },
                            "& .MuiOutlinedInput-root:hover": { backgroundColor: "rgba(0, 108, 119, 0.02)" },
                            "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: "0 0 0 3px rgba(0, 108, 119, 0.10)" },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#006C77" },
                        }}
                    >
                        {!loading ? (
                            <>
                                {!info2Expanded && (
                                    <>
                                        <Box className="col-sm-12 col-md-7 d-none d-md-flex">
                                            <Typography variant="body1" sx={{ fontWeight: "bolder" }}>
                                                Identificação
                                            </Typography>
                                        </Box>
                                        <Box
                                            className="col-sm-12 col-md-5"
                                            style={{ display: "flex", justifyContent: "end" }}
                                        >
                                            {selectedSecretary?.name && (
                                                <>
                                                    <Chip
                                                        style={{
                                                            color: selectedSecretary.activated
                                                                ? "#00B700"
                                                                : "#dc3545",
                                                            marginRight: 5,
                                                            width: "50%",
                                                            fontWeight: "bolder",
                                                        }}
                                                        clickable={false}
                                                        onClick={() => { }}
                                                        label={
                                                            selectedSecretary.activated ? "Ativo" : "Inativo"
                                                        }
                                                    />
                                                    {selectedSecretary.activated && (
                                                        <Chip
                                                            style={{
                                                                color: "black",
                                                                marginRight: 5,
                                                                width: "50%",
                                                            }}
                                                            disabled={((location?.state?.permissions?.canEdit === false) || (location?.state?.canEdit === false))}
                                                            onClick={() => {
                                                                setInEdit(true);
                                                            }}
                                                            label="Editar"
                                                            icon={
                                                                <DriveFileRenameOutlineOutlined
                                                                    style={{
                                                                        height: 24,
                                                                        width: 24,
                                                                        fill: "black",
                                                                    }}
                                                                />
                                                            }
                                                        />
                                                    )}
                                                    {false && (
                                                        <Chip
                                                            style={{
                                                                color: "black",
                                                                marginRight: 5,
                                                                width: "50%",
                                                            }}
                                                            disabled={true}
                                                            onClick={() => {
                                                                if (!isToastVisible) {
                                                                    handleClose();
                                                                }
                                                            }}
                                                            label="Excluir"
                                                            icon={
                                                                <DeleteForeverOutlined
                                                                    style={{
                                                                        height: 24,
                                                                        width: 24,
                                                                        fill: "black",
                                                                    }}
                                                                />
                                                            }
                                                        />
                                                    )}

                                                    <Chip
                                                        style={{
                                                            color: "black",
                                                            marginRight: 5,
                                                            width: "50%",
                                                        }}
                                                        disabled={(location?.state?.permissions?.canEdit === false) || (location?.state?.canEdit === false)}
                                                        onClick={() => {
                                                            if (!isToastVisible) {
                                                                verifyBeforeUpdateStatus(
                                                                    selectedSecretary.id,
                                                                    selectedSecretary.activated
                                                                        ? "deactivate"
                                                                        : "activate"
                                                                );
                                                            }
                                                        }}
                                                        label={
                                                            selectedSecretary.activated ? "Desativar" : "Ativar"
                                                        }
                                                        icon={
                                                            <RemoveCircleOutlineOutlined
                                                                style={{ height: 24, width: 24, fill: "black" }}
                                                            />
                                                        }
                                                    />
                                                </>
                                            )}
                                        </Box>
                                    </>
                                )}
                                <Accordion
                                    expanded={infoExpanded}
                                    style={{ padding: 0, margin: 0, boxShadow: "none" }}
                                >
                                    <AccordionSummary
                                        style={{ margin: 0, boxShadow: "none", display: "none" }}
                                        aria-controls="panel2-content"
                                        id="panel2-header"
                                    ></AccordionSummary>
                                    <AccordionDetails>
                                        <Box className="row">
                                            <Box
                                                className="col-md-12"
                                                style={{ margin: "15px 0px 15px 0px" }}
                                            >
                                                <FormControl
                                                    className="form-group"
                                                    style={{ display: "flex", width: "100%" }}
                                                >
                                                    <InputLabel
                                                        id="demo-multiple-name-label"
                                                        shrink
                                                        disabled={
                                                            (selectedSecretary?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                        style={{ fontFamily: "Prometo" }}
                                                        required
                                                    >
                                                        Nome
                                                    </InputLabel>
                                                    <TextField
                                                        name="name"
                                                        InputProps={{
                                                            label: "Nome",
                                                            notched: true,
                                                            style: { fontFamily: "Prometo" },
                                                        }}
                                                        disabled={
                                                            (selectedSecretary?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                        value={secretaryName}
                                                        onChange={(
                                                            event: ChangeEvent<HTMLInputElement>
                                                        ) => {
                                                            setSecretaryName(event.target.value);
                                                        }}
                                                        type="text"
                                                        autoComplete="off"
                                                        className="form-control form-control-login"
                                                        placeholder="Digite o nome"
                                                        required
                                                    />
                                                    <IconButton
                                                        className="visibility-password"
                                                        disabled
                                                        onMouseDown={(event: SyntheticEvent) => { }}
                                                        edge="end"
                                                        size="large"
                                                    >
                                                        <PersonOutlineOutlined />
                                                    </IconButton>
                                                </FormControl>
                                            </Box>

                                            <Box
                                                className="col-md-12"
                                                style={{ margin: "15px 0px 15px 0px" }}
                                            >
                                                <FormControl
                                                    className="form-group"
                                                    style={{ display: "flex", width: "100%" }}
                                                >
                                                    <InputLabel
                                                        id="demo-multiple-name-label"
                                                        shrink
                                                        disabled={
                                                            (selectedSecretary?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                        style={{ fontFamily: "Prometo" }}
                                                    >
                                                        Nome fantasia
                                                    </InputLabel>
                                                    <TextField
                                                        name="reducedName"
                                                        InputProps={{
                                                            label: "Nome fantasia",
                                                            notched: true,
                                                            style: { fontFamily: "Prometo" },
                                                        }}
                                                        disabled={
                                                            (selectedSecretary?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                        value={secretaryReducedName}
                                                        onChange={(
                                                            event: ChangeEvent<HTMLInputElement>
                                                        ) => {
                                                            setSecretaryReducedName(event.target.value);
                                                        }}
                                                        type="text"
                                                        autoComplete="off"
                                                        className="form-control form-control-login"
                                                        placeholder="Digite o nome fantasia (Opcional)"
                                                    />
                                                    <IconButton
                                                        className="visibility-password"
                                                        disabled
                                                        onMouseDown={(event: SyntheticEvent) => { }}
                                                        edge="end"
                                                        size="large"
                                                    >
                                                        <PersonOutlineOutlined />
                                                    </IconButton>
                                                </FormControl>
                                            </Box>

                                            <Box
                                                className="col-md-6"
                                                style={{ margin: "15px 0px 15px 0px" }}
                                            >
                                                <FormControl
                                                    className="form-group"
                                                    style={{ display: "flex", width: "100%" }}
                                                >
                                                    <InputLabel
                                                        id="demo-multiple-cpf-label"
                                                        shrink
                                                        disabled={
                                                            (selectedSecretary?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                    >
                                                        CNPJ
                                                    </InputLabel>
                                                    <TextField
                                                        name="cnpj"
                                                        type="cnpj"
                                                        autoComplete="off"
                                                        inputProps={{ maxLength: 14 }}
                                                        InputProps={{ label: "CNPJ", notched: true }}
                                                        placeholder="Digite o CNPJ (Opcional)"
                                                        className="form-control secretary-cnpj"
                                                        value={CNPJFormatter(secretaryCnpj)}
                                                        onChange={(
                                                            event: React.ChangeEvent<HTMLInputElement>
                                                        ) => {
                                                            setSecretaryCnpj(event.target.value);
                                                        }}
                                                        disabled={
                                                            (selectedSecretary?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                    />
                                                    <IconButton
                                                        className="visibility-password"
                                                        disabled
                                                        onMouseDown={(e: SyntheticEvent) => { }}
                                                        edge="end"
                                                        size="large"
                                                    >
                                                        <PinOutlined />
                                                    </IconButton>
                                                </FormControl>
                                            </Box>
                                            <Box
                                                className="col-md-6"
                                                style={{ margin: "15px 0px 15px 0px" }}
                                            >
                                                <FormControl
                                                    className="form-group"
                                                    style={{ display: "flex", width: "100%" }}
                                                >
                                                    <InputLabel
                                                        id="demo-multiple-cep-label"
                                                        required
                                                        shrink
                                                        disabled={
                                                            (selectedSecretary?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                    >
                                                        CEP
                                                    </InputLabel>
                                                    <TextField
                                                        name="cep"
                                                        type="text"
                                                        autoComplete="off"
                                                        required
                                                        InputProps={{ label: "CEP", notched: true }}
                                                        inputProps={{ maxLength: 8 }}
                                                        placeholder="Digite o cep"
                                                        className="form-control secretary-cep"
                                                        value={formatterZipCode(cep)}
                                                        error={cepAutofill.hasError}
                                                        helperText={cepAutofill.helperText}
                                                        onChange={(
                                                            event: React.ChangeEvent<HTMLInputElement>
                                                        ) => {
                                                            setCep(event.target.value);
                                                        }}
                                                        onBlur={cepAutofill.onBlur}
                                                        disabled={
                                                            (selectedSecretary?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                    />
                                                    <IconButton
                                                        className="visibility-password"
                                                        disabled
                                                        onMouseDown={(e: SyntheticEvent) => { }}
                                                        edge="end"
                                                        size="large"
                                                    >
                                                        <LocalAirportOutlined />
                                                    </IconButton>
                                                </FormControl>
                                            </Box>

                                            <Box
                                                className="col-md-12"
                                                style={{ margin: "15px 0px 15px 0px" }}
                                            >
                                                <FormControl
                                                    className="form-group"
                                                    required
                                                    style={{ display: "flex", width: "100%" }}
                                                >
                                                    <InputLabel
                                                        id="demo-multiple-address-label"
                                                        shrink
                                                        disabled={
                                                            (selectedSecretary?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                        style={{ fontFamily: "Prometo" }}
                                                    >
                                                        Endereço
                                                    </InputLabel>
                                                    <TextField
                                                        name="address"
                                                        required
                                                        InputProps={{
                                                            label: "Endereço",
                                                            notched: true,
                                                            style: { fontFamily: "Prometo" },
                                                        }}
                                                        disabled={
                                                            (selectedSecretary?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                        value={secretaryAddress}
                                                        onChange={(
                                                            event: ChangeEvent<HTMLInputElement>
                                                        ) => {
                                                            setSecretaryAddress(event.target.value);
                                                        }}
                                                        type="text"
                                                        autoComplete="off"
                                                        className="form-control form-control-login"
                                                        placeholder="Digite o endereço"
                                                    />
                                                    <IconButton
                                                        className="visibility-password"
                                                        disabled
                                                        onMouseDown={(e: SyntheticEvent) => { }}
                                                        edge="end"
                                                        size="large"
                                                    >
                                                        <PlaceOutlined />
                                                    </IconButton>
                                                </FormControl>
                                            </Box>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>

                                <Box
                                    className="crud-additional-info-bar col-sm-12 col-md-7 d-none d-md-flex"
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        cursor: "pointer",
                                        background: "#f3f4f6",
                                    }}
                                    onClick={() => {
                                        setInfo2Expanded(!info2Expanded);
                                        setInfoExpanded(!infoExpanded);
                                    }}
                                >
                                    <Typography variant="body1" sx={{ fontWeight: "bolder" }}>
                                        Informações adicionais
                                    </Typography>
                                    <Tooltip
                                        title={
                                            "Clique para adicionar informações adicionais sobre o cliente, contrato e etc."
                                        }
                                        style={{ color: "black", margin: 5, cursor: "pointer" }}
                                    >
                                        <HelpOutlineOutlined />
                                    </Tooltip>
                                </Box>

                                <Box
                                    className="crud-additional-info-bar col-sm-12 col-md-5"
                                    style={{
                                        display: "flex",
                                        justifyContent: "end",
                                        cursor: "pointer",
                                        background: "#f3f4f6",
                                    }}
                                    onClick={() => {
                                        setInfo2Expanded(!info2Expanded);
                                        setInfoExpanded(!infoExpanded);
                                    }}
                                >
                                    <IconButton onClick={() => { }} edge="end" size="large">
                                        <ArrowDropDownIcon
                                            style={{
                                                transform: info2Expanded ? "rotate(180deg)" : "",
                                            }}
                                        />
                                    </IconButton>
                                </Box>

                                <Accordion
                                    expanded={info2Expanded}
                                    style={{
                                        padding: 0,
                                        margin: 0,
                                        boxShadow: "none",
                                        marginTop: 5,
                                    }}
                                >
                                    <IconButton
                                        aria-label="toggle accordion"
                                        className="d-none"
                                        onClick={() => { }}
                                        edge="end"
                                        size="large"
                                    >
                                        <ArrowDropDownIcon />
                                    </IconButton>
                                    <AccordionDetails>
                                        <Box className="row">
                                            <Box
                                                className="col-md-6"
                                                style={{ margin: "15px 0px 20px 0px" }}
                                            >
                                                <FormControl
                                                    className="form-group"
                                                    style={{ display: "flex", width: "100%" }}
                                                >
                                                    <InputLabel
                                                        id="demo-multiple-name-label"
                                                        shrink
                                                        disabled={
                                                            (selectedSecretary?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                        required
                                                    >
                                                        Telefone
                                                    </InputLabel>
                                                    <TextField
                                                        name="phoneNumber"
                                                        type="tel"
                                                        autoComplete="off"
                                                        required
                                                        InputProps={{ label: "Telefone", notched: true }}
                                                        inputProps={{ maxLength: 11 }}
                                                        className="form-control phone"
                                                        placeholder="Digite o telefone"
                                                        inputMode="numeric"
                                                        value={phoneNumberFormatter(secretaryPhone)}
                                                        onChange={(
                                                            event: React.ChangeEvent<HTMLInputElement>
                                                        ) => {
                                                            setSecretaryPhone(event.target.value);
                                                        }}
                                                        disabled={
                                                            (selectedSecretary?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                    />
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        className="visibility-password"
                                                        disabled
                                                        onMouseDown={(e: SyntheticEvent) => { }}
                                                        edge="end"
                                                        size="large"
                                                    >
                                                        <PhoneOutlined />
                                                    </IconButton>
                                                </FormControl>
                                            </Box>

                                            <Box
                                                className="col-md-6"
                                                style={{ margin: "15px 0px 20px 0px" }}
                                            >
                                                <FormControl
                                                    className="form-group"
                                                    style={{ display: "flex", width: "100%" }}
                                                >
                                                    <InputLabel
                                                        id="demo-multiple-name-label"
                                                        shrink
                                                        disabled={
                                                            (selectedSecretary?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                    >
                                                        Telefone secundário
                                                    </InputLabel>
                                                    <TextField
                                                        name="phoneNumber2"
                                                        type="tel"
                                                        autoComplete="off"
                                                        InputProps={{ label: "Telefone secundário", notched: true }}
                                                        inputProps={{ maxLength: 11 }}
                                                        className="form-control phone"
                                                        placeholder="Digite o telefone secundário (Opcional)"
                                                        inputMode="numeric"
                                                        value={phoneNumberFormatter(secretaryPhone2)}
                                                        onChange={(
                                                            event: React.ChangeEvent<HTMLInputElement>
                                                        ) => {
                                                            setSecretaryPhone2(event.target.value);
                                                        }}
                                                        disabled={
                                                            (selectedSecretary?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                    />
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        className="visibility-password"
                                                        disabled
                                                        onMouseDown={(e: SyntheticEvent) => { }}
                                                        edge="end"
                                                        size="large"
                                                    >
                                                        <PhoneOutlined />
                                                    </IconButton>
                                                </FormControl>
                                            </Box>

                                            <Box
                                                className="col-md-12"
                                                style={{ margin: "15px 0px 20px 0px" }}
                                            >
                                                <FormControl
                                                    className="form-group"
                                                    style={{ display: "flex", width: "100%" }}
                                                >
                                                    <InputLabel
                                                        id="demo-multiple-mail-label"
                                                        required
                                                        shrink
                                                        disabled={
                                                            (selectedSecretary?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                    >
                                                        E-mail
                                                    </InputLabel>
                                                    <TextField
                                                        name="email"
                                                        type="email"
                                                        autoComplete="off"
                                                        required
                                                        InputProps={{ label: "E-mail", notched: true }}
                                                        placeholder="Digite o e-mail"
                                                        className="form-control form-control-login"
                                                        disabled={
                                                            (selectedSecretary?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                        value={secretaryEmail}
                                                        onChange={(
                                                            event: React.ChangeEvent<HTMLInputElement>
                                                        ) => {
                                                            setSecretaryEmail(event.target.value);
                                                        }}
                                                    />
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        className="visibility-password"
                                                        disabled
                                                        onMouseDown={(e: SyntheticEvent) => { }}
                                                        edge="end"
                                                        size="large"
                                                    >
                                                        <EmailOutlined />
                                                    </IconButton>
                                                </FormControl>
                                            </Box>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            </>
                        ) : (
                            loadingEsqueleton
                        )}
                    </Box>
                    <Box
                        className="tw-flex tw-w-full tw-flex-none tw-justify-end tw-border-t tw-border-black/10 tw-bg-white tw-p-4"
                        sx={{ display: "flex", width: "100%", alignSelf: "stretch", justifyContent: "flex-end", padding: 2, borderTop: "1px solid rgba(0, 0, 0, 0.10)", backgroundColor: "#ffff" }}
                    >
                        <Paper
                            className="tw-flex tw-w-full tw-flex-col-reverse tw-gap-2 sm:tw-w-auto sm:tw-flex-row"
                            elevation={0}
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column-reverse", sm: "row" },
                                justifyContent: "flex-end",
                                width: { xs: "100%", sm: "auto" },
                                marginLeft: "auto",
                                alignSelf: "flex-end",
                                gap: 1,
                                background: "transparent",
                            }}
                        >
                            <Button
                                type="button"
                                style={{
                                    fontFamily: "Prometo",
                                    color: "#ffff",
                                    marginRight: 5,
                                }}
                                onClick={() => {
                                    if (!isToastVisible) {
                                        verifyBeforeCancel(selectedSecretary);
                                    }
                                }}
                                className="btn btn-primary btn-block login-button-close tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                            >
                                <CloseOutlined style={{ marginRight: 5 }} />
                                <Typography
                                    className="forgot-password"
                                    style={{
                                        fontFamily: "Prometo",
                                        fontSize: "16px",
                                        color: "#ffff",
                                    }}
                                >
                                    Fechar
                                </Typography>
                            </Button>
                            <Button
                                type="button"
                                style={{ fontFamily: "Prometo", color: "#ffff" }}
                                disabled={!inEdit && !inInsert}
                                onClick={() => {
                                    inInsert && !inEdit ? onSave() : onUpdate();
                                }}
                                className="btn btn-primary btn-block login-button tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                            >
                                <SaveOutlined style={{ marginRight: 5 }} />
                                <Typography
                                    className="forgot-password"
                                    style={{
                                        fontFamily: "Prometo",
                                        fontSize: "16px",
                                        color: "#ffff",
                                    }}
                                >
                                    Salvar
                                </Typography>
                            </Button>
                        </Paper>
                    </Box>
                </Paper>
            </Rmodal>
        );
    }, [
        cepAutofill.hasError,
        cepAutofill.helperText,
        cepAutofill.onBlur,
        handleClose,
        verifyBeforeCancel,
        secretaryName,
        secretaryReducedName,
        secretaryCnpj,
        cep,
        secretaryAddress,
        secretaryPhone,
        secretaryPhone2,
        secretaryEmail,
        verifyBeforeUpdateStatus,
        infoExpanded,
        info2Expanded,
        location?.state?.permissions?.canDelete,
        location?.state?.permissions?.canEdit,
        location?.state?.canEdit,
        isToastVisible,
        validationText,
        selectedSecretary,
        isOpenDetails,
        loading,
        loadingEsqueleton,
        inInsert,
        inEdit,
        onSave,
        onUpdate
    ]);

    const getCustomersList = useCallback(async () => {
        const token = cookies.token || sessionStorage.getItem("token");
        try {
            const response = await customerFindAllListResponse(token, cookies.selectedParameters.customer.id);
            setCustomersList(response.data);
        } catch (error) {
            console.log(error);
            setCustomersList([]);
        }
    }, [cookies.token, cookies.selectedParameters.customer.id]);

    useEffect(() => {
        if (getCurrentUser.configs.isManager && (location?.state?.customerId === undefined)) {
            getCustomersList();
        }
    }, [getCustomersList, getCurrentUser.configs.isManager, location?.state?.customerId]);

    const customerParam =
        <>
            <Box className="col-md-12">
                <Autocomplete
                    id="chip-label-select-customer"
                    className="input-select-customer"
                    style={{
                        width: "100%",
                        minHeight: "56px",
                        height: "56px",
                        backgroundColor: "#ffffff29",
                        borderColor: "lightgray",
                    }}
                    options={customersList}
                    value={customer}
                    onChange={(_, option) => {
                        setCustomer(option ?? customerDefault);
                    }}
                    disabled={!getCurrentUser.configs.isManager}
                    getOptionLabel={(option) => `${option.reducedName}`}
                    noOptionsText={"Nada encontrado"}
                    renderInput={(params) => (
                        <>
                            <FormControl
                                className="form-group"
                                style={{ display: "flex" }}
                            >
                                <InputLabel
                                    id="country-label"
                                    required
                                    style={{ color: "rgba(0, 0, 0, 0.6)", fontFamily: "Prometo" }}
                                    shrink
                                >
                                    Cliente
                                </InputLabel>
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    placeholder="Selecione um cliente"
                                    style={{ background: "#ffff", borderRadius: 5 }}
                                    autoComplete="off"
                                    required
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        ...params.InputProps,
                                        notched: true,
                                        label: "Cliente",
                                        classes: {
                                            notchedOutline: "custom-notch",
                                        },
                                    }}
                                />
                            </FormControl>
                        </>
                    )}
                />
            </Box>
        </>;

    return (
        <>
            {DetailsModal}
            <Toast
                ref={toastRef}
                position="center"
                onShow={() => {
                    setIsToastVisible(true);
                    if (validationText) {
                        setTimeout(() => {
                            setFieldsIsValid(true);
                            setIsToastVisible(false);
                        }, 3000);
                    }
                }}
            />
            <GridTemplate
                columns={columns}
                headerName={"Secretarias"}
                isHiddenMenu={isHiddenMenu}
                isLoading={loading}
                order={"asc"}
                rows={rowsOptions(filteredList, getCurrentUser.permissions, (route: string, secretaryId: number, permissions: PermissionsType) => {
                    goToRoute(route, { permissions, customerId: location?.state?.customerId, secretaryId, secretaryName: secretariesList.find(it => it.id === secretaryId)?.name });
                }, (location?.state?.customerId ? true : false))}
                sortField="name"
                withFilter={true}
                withOptions={true}
                query={query}
                setQuery={(value: string) => setQuery(value)}
                filterPlaceholder={"Nome fantasia, CNPJ ou E-mail"}
                onRefresh={getAllSecretaries}
                onExport={getReport}
                disableAddButton={((location?.state?.permissions?.canCreate === false) || (location?.state?.canCreate === false))}
                handleNext={(activated: string, page: number, size: number) => getAllSecretaries(activated, page, size)}
                handlePrevious={() => { }}
                isLastPage={isLastPage}
                totalPerPage={numberOfElementsPerPage}
                currentPage={pageNumber}
                totalElements={numberOfElements}
                onAdd={() => {
                    setIsOpenDetails(true);
                    setInInsert(true);
                    setLoading(true);
                    setTimeout(() => {
                        setLoading(false);
                    }, 2000);
                }}
                onClickRow={(item: any) => {
                    setIsOpenDetails(true);
                    setSelectedSecretary(item);
                    handleOnEditValues(item);
                    setLoading(true);
                    setTimeout(() => {
                        setLoading(false);
                    }, 2000);
                }}
                withCustomerParameter={(location?.state?.customerId ? false : true)}
                customerParameter={customerParam}
            />
        </>
    );
};

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    display: "flex",
    flexDirection: "column",
    width: { xs: "calc(100vw - 24px)", sm: "min(94vw, 1100px)" },
    maxWidth: 1100,
    maxHeight: "calc(100dvh - 24px)",
    overflow: "hidden",
    bgcolor: "#ffff",
    border: "none",
    boxShadow: "none",
    borderRadius: 4,
    color: "black",
    justifyContent: "center",
    alignItems: "center",
    lineBreak: "anywhere",
    padding: 0,
    fontFamily: "Prometo",
};

export default SecretaryPage;
